import { useState } from "react";
import type {
  ContactInput,
  PaymentMode,
  StudentContact,
  StudentGoal,
  StudentProfile,
  StudentSubject,
  UpdateStudentProfileRequest,
} from "@edu/shared";
import {
  CONTACTS_PER_STUDENT_MAX,
  CONTACT_CHANNEL_TITLES,
  ContactChannel,
  GOAL_TITLES,
  PAYMENT_MODE_TITLES,
  PaymentMode as PaymentModeSchema,
  StudentGoal as StudentGoalSchema,
  StudentSubject as StudentSubjectSchema,
  STUDENT_SUBJECT_TITLES,
  STUDENT_NOTE_MAX,
  telHref,
} from "@edu/shared";

/**
 * Анкета ученика: кто он, с кем связываться и как платят.
 *
 * ⚠️ **Всё содержимое этой панели преподавательское и ученику не уезжает ни
 * одной ручкой.** Контакты матери это персональные данные третьего лица, а
 * заметка это то, что пишут ПРО ученика: «тянет с оплатой» стоит первым
 * примером в разделе 9 `docs/schedule-design.md`. Под заметкой так и написано,
 * что её видит только преподаватель, ровно как под `note` серии расписания.
 *
 * Панель стоит ПОСЛЕ занятий и учёта, а не первой: сюда заходят при заведении
 * ученика и потом раз в полгода, а занятия смотрят каждый день. Тот же порядок
 * «по частоте вопросов, а не по важности решений», по которому 18.08.2026
 * перестроили всю карточку.
 */
export function StudentProfilePanel(props: {
  profile: StudentProfile;
  busy: boolean;
  paymentsTracked: boolean;
  onSave: (patch: UpdateStudentProfileRequest) => void;
  onAddContact: (input: ContactInput) => void;
  onEditContact: (contactId: string, input: ContactInput) => void;
  onRemoveContact: (contactId: string) => void;
}) {
  const p = props.profile;

  return (
    <section className="card">
      <h2>Анкета</h2>

      <div className="form form--row">
        <Select
          label="Класс"
          value={p.grade === null ? "" : String(p.grade)}
          options={GRADES}
          empty="не указан"
          disabled={props.busy}
          onChange={(v) => props.onSave({ grade: v === "" ? null : Number(v) })}
        />
        <Select
          label="Предмет"
          value={p.subject ?? ""}
          options={StudentSubjectSchema.options.map((v) => [v, STUDENT_SUBJECT_TITLES[v]] as const)}
          empty="не указан"
          disabled={props.busy}
          onChange={(v) => props.onSave({ subject: v === "" ? null : (v as StudentSubject) })}
        />
        <Select
          label="Цель"
          value={p.goal ?? ""}
          options={StudentGoalSchema.options.map((v) => [v, GOAL_TITLES[v]] as const)}
          empty="не указана"
          disabled={props.busy}
          onChange={(v) => props.onSave({ goal: v === "" ? null : (v as StudentGoal) })}
        />
      </div>

      <div className="form form--row">
        {/*
          Режим оплаты виден только тому, кто учёт оплат включил: иначе это
          выбор, который ни на что не влияет, а таких на экране быть не должно.
        */}
        {props.paymentsTracked && (
          <Select
            label="Оплата"
            value={p.paymentMode}
            options={PaymentModeSchema.options.map((v) => [v, PAYMENT_MODE_TITLES[v]] as const)}
            disabled={props.busy}
            onChange={(v) => props.onSave({ paymentMode: v as PaymentMode })}
          />
        )}
        {/*
          ⚠️ Тариф нарисован, но выключен, и колонки под него в базе НЕТ. Это
          решение владельца 21.08.2026: поле обещает работу, которая идёт, а
          колонка, которую ничто не пишет, это плата без покупки. Приедут они
          вместе, одной правкой, с правилом пересчёта суммы.
        */}
        <label className="field">
          <span className="lbl">Тариф за занятие</span>
          <input
            className="fld"
            type="text"
            value=""
            disabled
            placeholder="—"
            title="Работаем над этим: суммы продукт пока не считает"
            readOnly
          />
        </label>
      </div>

      <Contacts
        contacts={p.contacts}
        busy={props.busy}
        onAdd={props.onAddContact}
        onEdit={props.onEditContact}
        onRemove={props.onRemoveContact}
      />

      <Note value={p.note ?? ""} busy={props.busy} onSave={(note) => props.onSave({ note })} />
    </section>
  );
}

const GRADES: readonly (readonly [string, string])[] = Array.from({ length: 11 }, (_, i) => [
  String(i + 1),
  `${i + 1} класс`,
]);

/**
 * Выпадающий список, сохраняющий сразу по выбору.
 *
 * Без кнопки «Сохранить», потому что выбор из списка это законченное действие:
 * промежуточного состояния, которое можно передумать, у него нет, в отличие от
 * набранного текста.
 */
function Select(props: {
  label: string;
  value: string;
  options: readonly (readonly [string, string])[];
  empty?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span className="lbl">{props.label}</span>
      <select
        className="fld"
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
      >
        {props.empty !== undefined && <option value="">{props.empty}</option>}
        {props.options.map(([value, title]) => (
          <option key={value} value={value}>
            {title}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ──────────────────────────── контакты ─────────────────────────────── */

/**
 * Записная книжка пары.
 *
 * Строка это ЧЕЛОВЕК, а не канал: у матери бывает и телефон, и телеграм, и это
 * один собеседник. Разложив их по строкам, мы получили бы четыре строки на двух
 * людей, и «кому звонить» перестало бы читаться взглядом.
 */
function Contacts(props: {
  contacts: readonly StudentContact[];
  busy: boolean;
  onAdd: (input: ContactInput) => void;
  onEdit: (contactId: string, input: ContactInput) => void;
  onRemove: (contactId: string) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const full = props.contacts.length >= CONTACTS_PER_STUDENT_MAX;

  return (
    <div className="stu__contacts">
      <h3 className="lbl">Связь</h3>

      {props.contacts.length === 0 && !adding && (
        <p className="muted">
          Контактов нет. Обычно это телефон родителя и то, чем с ним удобнее переписываться.
        </p>
      )}

      <ul className="cnt">
        {props.contacts.map((c) =>
          editing === c.id ? (
            <li key={c.id}>
              <ContactForm
                initial={c}
                busy={props.busy}
                onCancel={() => setEditing(null)}
                onSubmit={(input) => {
                  props.onEdit(c.id, input);
                  setEditing(null);
                }}
              />
            </li>
          ) : (
            <ContactRow
              key={c.id}
              contact={c}
              busy={props.busy}
              onEdit={() => setEditing(c.id)}
              onRemove={() => props.onRemove(c.id)}
            />
          ),
        )}
      </ul>

      {adding ? (
        <ContactForm
          busy={props.busy}
          onCancel={() => setAdding(false)}
          onSubmit={(input) => {
            props.onAdd(input);
            setAdding(false);
          }}
        />
      ) : (
        <div className="acts">
          <button
            type="button"
            className="btn"
            disabled={props.busy || full}
            title={full ? `Больше ${CONTACTS_PER_STUDENT_MAX} контактов не храним` : undefined}
            onClick={() => setAdding(true)}
          >
            Добавить контакт
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Контакт строкой.
 *
 * ⚠️ Телефон это ссылка `tel:` только тогда, когда из него собирается
 * набираемый номер (`telHref`). Строка, из которой не собирается, остаётся
 * текстом: битая ссылка хуже её отсутствия.
 *
 * ⚠️ Кнопки «написать» здесь нет и не будет, пока у продукта нет разрешённого
 * транспорта уведомлений: Telegram для критичного запрещён разделом 4 CLAUDE.md.
 * Это справочник «как связаться», а не отправка.
 */
function ContactRow(props: {
  contact: StudentContact;
  busy: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const c = props.contact;
  const tel = telHref(c.phone);

  return (
    <li className="cnt__row">
      <span className="cnt__who">
        <b>{c.name}</b>
        {c.note !== null && <span className="row__sub">{c.note}</span>}
      </span>

      <span className="cnt__how">
        {c.phone !== null &&
          (tel === null ? (
            <span>{c.phone}</span>
          ) : (
            <a className="textlink" href={tel}>
              {c.phone}
            </a>
          ))}
        {c.link !== null && (
          <a className="textlink" href={c.link} target="_blank" rel="noreferrer noopener">
            профиль
          </a>
        )}
        {c.channels.length > 0 && (
          <span className="muted">{c.channels.map((k) => CONTACT_CHANNEL_TITLES[k]).join(", ")}</span>
        )}
      </span>

      <span className="row__act">
        <button type="button" className="btn btn--sm" disabled={props.busy} onClick={props.onEdit}>
          Править
        </button>
        <button type="button" className="btn btn--sm" disabled={props.busy} onClick={props.onRemove}>
          Удалить
        </button>
      </span>
    </li>
  );
}

/** Форма контакта. Правится целиком: стереть телефон иначе было бы нечем. */
function ContactForm(props: {
  initial?: StudentContact;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: ContactInput) => void;
}) {
  const [name, setName] = useState(props.initial?.name ?? "");
  const [phone, setPhone] = useState(props.initial?.phone ?? "");
  const [link, setLink] = useState(props.initial?.link ?? "");
  const [note, setNote] = useState(props.initial?.note ?? "");
  const [channels, setChannels] = useState<readonly ContactChannel[]>(
    props.initial?.channels ?? [],
  );

  const toggle = (k: ContactChannel): void =>
    setChannels((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit({
          name: name.trim(),
          // Пустую строку шлём как есть: в `null` её превращает сервер, в одном
          // месте. Два приведения однажды разъедутся
          phone: phone.trim() === "" ? null : phone.trim(),
          link: link.trim() === "" ? null : link.trim(),
          note: note.trim() === "" ? null : note.trim(),
          channels: [...channels],
        });
      }}
    >
      <label className="field">
        <span className="lbl">Кто это</span>
        <input
          className="fld"
          value={name}
          maxLength={80}
          required
          placeholder="Ирина, мама"
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div className="form form--row">
        <label className="field">
          <span className="lbl">Телефон</span>
          <input
            className="fld"
            type="tel"
            value={phone}
            maxLength={32}
            placeholder="+7 916 123-45-67"
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="lbl">Ссылка на профиль</span>
          <input
            className="fld"
            type="url"
            value={link}
            maxLength={2000}
            placeholder="https://vk.com/…"
            onChange={(e) => setLink(e.target.value)}
          />
        </label>
      </div>

      {/* Галочки, а не список: у одного человека законно и телеграм, и звонок */}
      <fieldset className="field cnt__ch">
        <legend className="lbl">Чем связываться</legend>
        {ContactChannel.options.map((k) => (
          <label key={k} className="form__check">
            <input type="checkbox" checked={channels.includes(k)} onChange={() => toggle(k)} />
            {CONTACT_CHANNEL_TITLES[k]}
          </label>
        ))}
      </fieldset>

      <label className="field">
        <span className="lbl">Пометка</span>
        <input
          className="fld"
          value={note}
          maxLength={300}
          placeholder="звонить после 18:00"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="form__foot">
        <button type="button" className="btn btn--quiet" onClick={props.onCancel}>
          Отмена
        </button>
        <button type="submit" className="btn" disabled={props.busy || name.trim() === ""}>
          Сохранить
        </button>
      </div>
    </form>
  );
}

/* ───────────────────────────── заметка ─────────────────────────────── */

/**
 * Приватная заметка о ученике.
 *
 * С кнопкой «Сохранить», в отличие от списков выше: набранный текст это
 * промежуточное состояние, и сохранять его по каждому нажатию клавиши значит
 * слать запрос на букву. Кнопка гаснет, пока текст не менялся, чтобы «сохранено
 * ли» было видно без всякой подписи.
 */
function Note(props: { value: string; busy: boolean; onSave: (note: string) => void }) {
  const [text, setText] = useState(props.value);
  const dirty = text !== props.value;

  return (
    <div className="form">
      <label className="field">
        <span className="lbl">Заметка</span>
        <textarea
          className="fld"
          rows={3}
          value={text}
          maxLength={STUDENT_NOTE_MAX}
          placeholder="Что важно помнить об этом ученике"
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {/*
        ⚠️ Подпись обязательна и она не украшение: это то же правило, что под
        `note` серии расписания. В такие поля пишут то, чего вторая сторона
        видеть не должна, и человек имеет право знать это до того, как напишет.
      */}
      <p className="hint">Видите только вы. Ученику заметка не показывается.</p>
      <div className="form__foot">
        <button
          type="button"
          className="btn"
          disabled={props.busy || !dirty}
          onClick={() => props.onSave(text)}
        >
          Сохранить заметку
        </button>
      </div>
    </div>
  );
}
